// useResilientList — the one reliable list-fetch pattern, in one place.
//
// Every list view in the OS needs the same protections against the recurring
// "stuck on skeletons / blank screen" wedge (auth gate stalls, half-open
// sockets after sleep, cold caches). Until now each hook hand-rolled them and
// coverage was patchy. This hook bakes them ALL in so a new list can't ship
// without them:
//
//   1. Cache-first paint   — render last-known rows instantly, never blank.
//   2. Bounded loading     — the query is raced against a timeout so a hung
//                            connection can't pin the UI on skeletons forever.
//   3. PGlite fallback     — on timeout/error with a cold cache, serve locally
//                            -synced rows (offline-first) instead of empty.
//   4. Stale-skeleton watchdog — force loading off if it ever outlives the
//                            timeout (covers a query that neither resolves nor
//                            rejects — the failure try/catch can't see).
//   5. Cache-hydrated repaint — paint cache the moment IndexedDB finishes
//                            hydrating after a cold reload.
//   6. Auto-refresh        — silent poll + refetch on tab focus.
//
// Usage:
//   const { items, loading, error, refetch } = useResilientList({
//     cacheKey: `deals:${status}`,
//     queryFn: () => supabase.from('deals').select('*', { count: 'estimated' })
//                            .eq('status', status),
//     table: 'deals',                          // PGlite fallback table (optional)
//     localOptions: { orderBy: 'created_at DESC' },
//   })
//
// queryFn must return a thenable resolving to { data, error, count } (a raw
// Supabase builder does). It is read from a ref, so passing a fresh inline
// arrow every render is safe — the fetch identity is keyed on cacheKey, which
// must encode every variable input the query depends on.

import { useState, useEffect, useCallback, useRef } from 'react'
import { getCached, setCached } from '../lib/queryCache'
import { useAutoRefresh } from './useAutoRefresh'
import { localFallback, localFirstRows, localUpsert } from '../lib/syncEngine'

const DEFAULT_TIMEOUT_MS = 15000
const WATCHDOG_MS = 12000

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ])
}

export function useResilientList({
  cacheKey,
  queryFn,
  table = null,
  localOptions = {},
  transform = null,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  refreshIntervalMs = 60000,
  autoRefresh = true,
  enabled = true,
}) {
  const cached = getCached(cacheKey)
  const [items, setItems]   = useState(cached || [])
  const [total, setTotal]   = useState(cached?.length || 0)
  const [loading, setLoading] = useState(enabled && !cached)
  const [error, setError]   = useState(null)

  // queryFn/transform live in refs so inline arrows don't re-key the fetch
  // (which would refetch every render). cacheKey is the real identity. Refs are
  // updated in an effect (not during render) and only read inside the async
  // fetch, so fetch always sees the latest closure.
  const queryFnRef   = useRef(queryFn)
  const transformRef = useRef(transform)
  useEffect(() => { queryFnRef.current = queryFn; transformRef.current = transform })
  // settledRef lets the watchdog tell "still genuinely in flight" from "done".
  const settledRef = useRef(false)
  // Which cacheKey has already had a local-first paint (avoid re-painting on
  // every offline poll; keyed so navigating to a new key paints again).
  const paintedLocalRef = useRef(null)
  const localOptsKey = JSON.stringify(localOptions)

  const fetch = useCallback(async (silent = false) => {
    if (!enabled) { setLoading(false); return }
    if (!silent && !getCached(cacheKey)) setLoading(true)
    setError(null)
    settledRef.current = false

    // Local-first paint (desktop shell only — localFirstRows returns null in
    // browsers): instant rows from the on-disk mirror while the network fetch
    // runs. Fire-and-forget in parallel so a slow local read never delays the
    // fetch. Guarded on network SUCCESS only (networkOk + cache check for a
    // later fetch's success) — a fast network FAILURE must not suppress a
    // slightly-slower local paint, or blocked/offline mounts render nothing.
    // Mirror rows are potentially stale, so (same rule as the fallback path)
    // they are never written to the network cache.
    let networkOk = false
    if (table && !silent && paintedLocalRef.current !== cacheKey && !getCached(cacheKey)?.length) {
      localFirstRows(table, localOptions).then((local) => {
        if (!local || networkOk || getCached(cacheKey)?.length) return
        paintedLocalRef.current = cacheKey
        const rows = transformRef.current ? transformRef.current(local) : local
        setItems(rows)
        setTotal(rows.length)
        setLoading(false)
      }).catch(() => {})
    }

    try {
      const { data, error: err, count } = await withTimeout(queryFnRef.current(), timeoutMs)
      if (err) throw err
      networkOk = true // fresh rows win; suppress any late local-first paint
      const raw = Array.isArray(data) ? data : []
      const rows = transformRef.current ? transformRef.current(raw) : raw

      // Write-through: mirror fresh rows into PGlite so a later offline/timeout
      // fallback can serve them. Fire-and-forget — never block the render.
      if (table) for (const r of raw) if (r?.id) localUpsert(table, r).catch(() => {})

      setItems(rows)
      setTotal(typeof count === 'number' ? count : rows.length)
      setCached(cacheKey, rows)
    } catch (e) {
      console.warn(`[useResilientList ${cacheKey}]`, e?.message)
      // Offline-first: only fall back when nothing is already showing (cold
      // cache) so a failed silent poll never replaces good in-memory data.
      // Fallback rows are stale, so they are NOT written back to the network
      // cache; error surfaces only when local is also empty.
      const cur = getCached(cacheKey)
      if ((!cur || cur.length === 0) && table) {
        try {
          const local = await localFallback(table, localOptions)
          const rows = transformRef.current ? transformRef.current(local) : local
          if (rows.length > 0) {
            setItems(rows); setTotal(rows.length); setError(null)
            return
          }
        } catch (fe) {
          console.warn(`[useResilientList ${cacheKey}] local fallback failed:`, fe?.message)
        }
      }
      setError(e)
    } finally {
      settledRef.current = true
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, table, timeoutMs, enabled, localOptsKey])

  useEffect(() => { fetch() }, [fetch])

  // Stale-skeleton watchdog — a query that never resolves OR rejects (the
  // wedged-client failure mode try/catch can't see) must not pin the UI on
  // skeletons. Force loading off; cache/fallback already painted what we have.
  useEffect(() => {
    if (!loading) return
    const t = setTimeout(() => { if (!settledRef.current) setLoading(false) }, WATCHDOG_MS)
    return () => clearTimeout(t)
  }, [loading])

  // Paint cache-first the moment the persistent cache finishes hydrating —
  // a cold mount after reload otherwise shows skeletons until the first fetch.
  useEffect(() => {
    const onHydrated = () => {
      const c = getCached(cacheKey)
      if (Array.isArray(c) && c.length) {
        setItems(prev => (prev.length ? prev : c))
        setLoading(false)
      }
    }
    window.addEventListener('chatrone:cache-hydrated', onHydrated)
    return () => window.removeEventListener('chatrone:cache-hydrated', onHydrated)
  }, [cacheKey])

  const silentRefetch = useCallback(() => fetch(true), [fetch])
  useAutoRefresh(silentRefetch, { intervalMs: refreshIntervalMs, enabled: autoRefresh && enabled })

  return { items, total, loading, error, refetch: fetch }
}
